import { ColumnDef, Table as TanStackTable, Updater } from "@tanstack/react-table";

/**
 * Table column alignment options
 */
export type TableColumnAlign = "left" | "center" | "right";

/**
 * Table sorting state
 */
export type SortDirection = "asc" | "desc" | false;

/**
 * Base column definition with common properties
 */
export type BaseColumnDef<T> = ColumnDef<T> & {
  align?: TableColumnAlign;
  enableSorting?: boolean;
  headerClassName?: string;
  cellClassName?: string;
}

/**
 * Table configuration options
 */
export interface TableConfig<T> {
  /**
   * Enable client-side sorting
   * @default true
   */
  enableSorting?: boolean;
  /**
   * Enable client-side pagination
   * @default false (server-side pagination is default)
   */
  enablePagination?: boolean;
  /**
   * Initial page size for client-side pagination
   * @default 10
   */
  pageSize?: number;
  /**
   * Enable row selection
   * @default false
   */
  enableRowSelection?: boolean;
  /**
   * Custom empty state message
   */
  emptyMessage?: string;
  /**
   * Custom loading state component
   */
  loadingComponent?: React.ReactNode;
  /**
   * Table height constraint
   * @default "max-h-[600px]"
   */
  height?: string;
  /**
   * Show table borders
   * @default true
   */
  showBorders?: boolean;
  /**
   * Enable zebra striping
   * @default false
   */
  enableZebraStriping?: boolean;
  /**
   * Disable horizontal scrolling for table container.
   * @default false
   */
  disableHorizontalScroll?: boolean;
}

/**
 * Server-side pagination state
 */
export interface ServerPaginationState {
  pageIndex: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/**
 * Table action handlers
 */
export interface TableActions<T> {
  onRowClick?: (row: T) => void;
  onRowSelect?: (row: T, selected: boolean) => void;
  onSortChange?: (columnId: string, direction: SortDirection) => void;
  onPageChange?: (page: number) => void;
}

/**
 * Status badge configuration
 */
export interface StatusBadgeConfig {
  background: string;
  textColor: string;
}

/**
 * Status styles mapping
 */
export interface StatusStyles {
  [key: string]: StatusBadgeConfig;
}

/**
 * Table styles configuration
 */
export interface TableStylesConfig {
  headerBg?: string;
  headerText?: string;
  rowHover?: string;
  statusStyles?: StatusStyles;
}

/**
 * Helper function to get alignment class
 */
export function getAlignClass(align?: TableColumnAlign): string {
  switch (align) {
    case "center":
      return "text-center";
    case "right":
      return "text-right";
    default:
      return "text-left";
  }
}

/**
 * Helper function to get sort icon
 */
export function getSortIcon(direction: SortDirection): "↑" | "↓" | "⇅" {
  switch (direction) {
    case "asc":
      return "↑";
    case "desc":
      return "↓";
    default:
      return "⇅";
  }
}

